import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { BookOpen, Compass, Scale, Target } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.1 } }
};

export const KGOBDifference = () => {
  const pillars = [
    {
      title: 'Clarity First',
      desc: 'We simplify complexity — transforming intricate tax code, audit requirements, and financial strategies into clear, actionable insights you can trust.',
      icon: BookOpen,
      testid: 'difference-clarity-card'
    },
    {
      title: 'Guidance Always',
      desc: 'We lead with empathy and expertise — providing strategic counsel that aligns with your goals, values, and vision for sustainable growth.',
      icon: Compass,
      testid: 'difference-guidance-card'
    },
    {
      title: 'Balance Built In',
      desc: 'We help clients thrive beyond the numbers — harmonizing compliance demands with strategic opportunities to build lasting success.',
      icon: Scale,
      testid: 'difference-balance-card'
    }
  ];

  return (
    <section 
      id="difference"
      className="relative overflow-hidden bg-[radial-gradient(1200px_500px_at_10%_10%,rgba(212,175,55,0.12),transparent_60%),linear-gradient(180deg,#FFFFFF_0%,#F7F6F2_100%)] py-16 lg:py-20"
      data-testid="kgob-difference-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="uppercase tracking-[0.18em] text-xs text-[#4C5A70] mb-3">
            THE KGOB DIFFERENCE
          </p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B2240]">
            What We Do Goes Beyond Accounting.
          </h2>
          <p className="mt-4 text-lg text-[#4C5A70] max-w-2xl mx-auto">
            We build balance — in business, in life, and in legacy. Behind every return and report is a relationship rooted in clarity, care, and confidence.
          </p>
        </motion.div>

        <motion.div 
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {pillars.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <motion.div key={pillar.title} variants={fadeUp}>
                <Card 
                  className="rounded-2xl p-6 glass-card h-full hover:shadow-[0_12px_40px_rgba(11,34,64,.18)] hover:border-white/40 transition-all duration-300"
                  data-testid={pillar.testid}
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#0B2240] mb-4">
                    <Icon className="h-6 w-6 text-[#D4AF37]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#0B2240] mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-[#4C5A70] leading-relaxed">
                    {pillar.desc}
                  </p>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};